import { Badge, Button, Text } from '@figma/fpl-components';
import { borderRadius, color, spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { cardMarker, cardSize, toolkitLayout } from './toolkit.stylex';

const ease = 'cubic-bezier(0.2, 0.65, 0.3, 1)';

const styles = stylex.create({
  root: {
    color: 'inherit',
    display: 'flex',
    flexDirection: 'column',
    gap: spacer.spacer2,
    outline: 'none',
    textDecoration: 'none',
  },
  picture: {
    aspectRatio: toolkitLayout.pictureAspectRatio,
    backgroundColor: color.bgSecondary,
    borderColor: color.border,
    borderRadius: borderRadius.large,
    borderStyle: 'solid',
    borderWidth: '1px',
    boxSizing: 'border-box',
    isolation: 'isolate',
    overflow: 'hidden',
    position: 'relative',
  },
  focusRing: {
    outline: {
      default: 'none',
      [stylex.when.ancestor(':focus-visible', cardMarker)]: `2px solid ${color.borderSelected}`,
    },
    outlineOffset: '-2px',
  },
  image: {
    height: '100%',
    inset: 0,
    objectFit: 'cover',
    position: 'absolute',
    width: '100%',
  },
  // The design's grey layer over the picture on hover. It is the same light black in both color modes.
  dim: {
    backgroundColor: 'rgb(0 0 0 / 0.1)',
    inset: 0,
    opacity: {
      default: 0,
      [stylex.when.ancestor(':hover', cardMarker)]: 1,
      [stylex.when.ancestor(':focus-visible', cardMarker)]: 1,
    },
    pointerEvents: 'none',
    position: 'absolute',
    transition: {
      default: `opacity 0.3s ${ease}`,
      '@media (prefers-reduced-motion: reduce)': 'none',
    },
  },
  action: {
    display: 'flex',
    justifyContent: 'center',
    left: 0,
    opacity: {
      default: 0,
      [stylex.when.ancestor(':hover', cardMarker)]: 1,
      [stylex.when.ancestor(':focus-visible', cardMarker)]: 1,
      '@media (hover: none)': 1,
    },
    pointerEvents: 'none',
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: {
      default: 'translateY(calc(-50% + 4px))',
      [stylex.when.ancestor(':hover', cardMarker)]: 'translateY(-50%)',
      [stylex.when.ancestor(':focus-visible', cardMarker)]: 'translateY(-50%)',
      '@media (hover: none)': 'translateY(-50%)',
    },
    transition: {
      default: `opacity 0.3s ${ease}, transform 0.3s ${ease}`,
      '@media (prefers-reduced-motion: reduce)': 'none',
    },
  },
  missingMark: {
    left: spacer.spacer3,
    position: 'absolute',
    top: spacer.spacer3,
  },
  words: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  text: {
    margin: 0,
  },
  twoLines: {
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    display: '-webkit-box',
    overflow: 'hidden',
  },
});

interface ToolCardProps {
  name: string;
  description: string;
  thumbnail?: string;
  /** Where the card goes when clicked. A card without one opens nothing and has no hover. */
  href?: string;
  /** A copy drawn only so a looping row can scroll on. Hidden from screen readers and the tab order. */
  isCopy?: boolean;
  /** Shows a "Tool not found" mark, for internal users only. */
  isToolMissing?: boolean;
}

/**
 * One tool in the catalog: its picture, with the name and description under it. The whole card is the
 * link, so "Use tool" is only a hover label and is kept out of the tab order and the accessibility tree.
 */
export function ToolCard({ name, description, thumbnail, href, isCopy = false, isToolMissing = false }: ToolCardProps) {
  const { t } = useTranslation();

  const content = (
    <>
      <div {...stylex.props(styles.picture, href ? styles.focusRing : null)}>
        {thumbnail ? <img {...stylex.props(styles.image)} src={thumbnail} alt="" decoding="async" /> : null}
        {href ? <div {...stylex.props(styles.dim)} /> : null}
        {isToolMissing ? (
          <div {...stylex.props(styles.missingMark)}>
            <Badge variant="warningOutline">{t(I18N_KEYS.TOOLKIT.TOOL_NOT_FOUND)}</Badge>
          </div>
        ) : null}
        {href ? (
          <div {...stylex.props(styles.action)} aria-hidden>
            <Button variant="inverse" tabIndex={-1}>
              {t(I18N_KEYS.TOOLKIT.USE_TOOL)}
            </Button>
          </div>
        ) : null}
      </div>
      <div {...stylex.props(styles.words)}>
        <Text.h3 {...stylex.props(styles.text)} variant="body-medium-strong" color="default" truncate>
          {name}
        </Text.h3>
        <Text.p {...stylex.props(styles.text, styles.twoLines)} variant="body-medium" color="secondary">
          {description}
        </Text.p>
      </div>
    </>
  );

  // A plain link, so the tool page opens with a full page load like every other way into /tool.
  return href ? (
    <a
      {...stylex.props(cardSize.card, styles.root, cardMarker)}
      href={href}
      aria-hidden={isCopy || undefined}
      tabIndex={isCopy ? -1 : undefined}
    >
      {content}
    </a>
  ) : (
    <div {...stylex.props(cardSize.card, styles.root)} aria-hidden={isCopy || undefined}>
      {content}
    </div>
  );
}
